import { useHistory } from "react-router";
import LyfeDataTable from "../../../components/LyfeTable/LyfeDataTable";
import { SimulatorUserRole } from "../../../models/generated/graphql";
import { Column } from "../../../types/tableTypes";
import { GetSimulationsQuery } from "../../data/types/queries.generated";
import { Box, Typography } from "@material-ui/core";
import FlexTypographyComponent from "../../../components/FlexTypographyComponent";
import { EMPTY_VALUE_PLACEHOLDER, HeaderActionOptions } from "../../../models/common/data";
import { localeDate } from "../../../utils/locale-date";
import LyfeAvatar from "../../../components/LyfeAvatar";
import { splitName } from "../../../utils/name-to-initials";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { userPersonalInfoState } from "../../../store/user";
import { simulationsActivePage, simulationsRowsPerPage, snackbarUpdateStatus } from "../../../store/simulator";
import { BusinessCaseInput } from "../comparison/components/BusinessCaseInput";
import { SimulationTableRowButtons } from "./SimulationTableRowButtons";
import { SimulationTableHeader } from "./SimulationTableHeader";
import { tableIdMinWidth } from "../../../hooks/use-map-theme-styles";
import { userOrganization } from "../../../store/layout";
import ErrorSnackbarAlert from "../../../components/errorMessages/ErrorSnackbarAlert";
import { SimulationStatus } from "./SimulationStatus";

const defaultSort = {
	column: "id",
	action: "sortDesc" as HeaderActionOptions,
};

interface ISimulationTableProps {
	loading: boolean;
	data: GetSimulationsQuery | undefined;
	comparisonSimulations: Array<string>;
	headerTitle?: string;
	isGroupView?: boolean;
	viewMoreState?: boolean;
	viewMoreLimit?: number;
	handleViewMoreClick?: (expanded: boolean) => void;
}

export const SimulationTable = (props: ISimulationTableProps) => {
	const {
		loading,
		data,
		comparisonSimulations,
		headerTitle,
		isGroupView = false,
		viewMoreLimit,
		viewMoreState,
		handleViewMoreClick,
	} = props;
	const title = "simulations";
	const { push } = useHistory();
	const { t } = useTranslation();
	const { simulatorProductInputEnabled } = useRecoilValue(userOrganization);
	const activeUser = useRecoilValue(userPersonalInfoState);
	const rowsPerPage = useRecoilValue(simulationsRowsPerPage);
	const page = useRecoilValue(simulationsActivePage);
	const businessCaseSnackBarUpdateStatus = useRecoilValue(snackbarUpdateStatus);
	const currentUserOwnerSimulations =
		data?.simulations
			.map((simulation) => {
				return { simulationId: simulation.id, role: simulation.simulationUserRole };
			})
			.filter(({ role }) => role === SimulatorUserRole.Owner) || [];

	const handleRowClick = (rowData: { id: string | number | undefined }) => {
		push(`/simulator/${rowData.id}`);
	};

	const columns = (t: any): Array<Column<GetSimulationsQuery["simulations"][number]>> =>
		[
			{
				title: t("simulation-id"),
				field: "id",
				render: (rawData: GetSimulationsQuery["simulations"][number]) => (
					<Typography style={{ minWidth: tableIdMinWidth }} variant="body2">
						{rawData.id}
					</Typography>
				),
			},
			!isGroupView && {
				title: t("product"),
				field: "product",
				render: (rowData: GetSimulationsQuery["simulations"][number]) => <FlexTypographyComponent text={rowData?.product || "--"} />,
			},
			{
				title: t("status"),
				field: "status",
				render: (rowData: GetSimulationsQuery["simulations"][number]) => <SimulationStatus status={rowData?.status} />,
			},
			{
				title: t("short-name"),
				field: "shortName",
				render: (rowData: GetSimulationsQuery["simulations"][number]) => (
					<FlexTypographyComponent text={rowData?.shortName || EMPTY_VALUE_PLACEHOLDER} />
				),
			},
			!isGroupView && {
				title: t("domain-header-general"),
				field: "domainName",
				render: (rowData: GetSimulationsQuery["simulations"][number]) => (
					<FlexTypographyComponent text={rowData?.domainName || "--"} />
				),
			},
			!isGroupView && {
				title: t("simulation-counterparty"),
				field: "counterparty",
				render: (rowData: GetSimulationsQuery["simulations"][number]) => (
					<FlexTypographyComponent text={rowData?.counterparty || EMPTY_VALUE_PLACEHOLDER} />
				),
			},
			{
				title: t("simulator-header-indications"),
				field: "indications",
				render: (rowData: GetSimulationsQuery["simulations"][number]) => <FlexTypographyComponent text={rowData?.indications || "--"} />,
			},
			isGroupView && {
				title: t("simulation-business-case"),
				field: "businessCase",
				render: (rowData: GetSimulationsQuery["simulations"][number], _name: any, _action: any) => (
					<BusinessCaseInput
						simulationId={rowData.id}
						value={rowData.businessCase || null}
						userRole={rowData.simulationUserRole || SimulatorUserRole.NonViewer}
					/>
				),
			},
			{
				title: t("model"),
				field: "model",
				render: (rowData: GetSimulationsQuery["simulations"][number]) => <FlexTypographyComponent text={rowData?.model || "--"} />,
			},
			{
				title: t("last-update"),
				field: "lastUpdatedOn",
				align: "right",
				render: (rowData: GetSimulationsQuery["simulations"][number]) => (
					<FlexTypographyComponent containerStyles={{ justifyContent: "right" }} text={localeDate(rowData?.lastUpdatedOn)!} />
				),
			},
			{
				title: t("added-by"),
				field: "createdByName",
				align: "center",
				render: (rowData: GetSimulationsQuery["simulations"][number]) => (
					<Box width="100%" textAlign={"center"}>
						<LyfeAvatar
							firstName={splitName(rowData?.createdByName || "")[0] || ""}
							lastName={splitName(rowData?.createdByName || "")[1] || ""}
							size="small"
							initialsProps={{ style: { fontSize: "14px" } }}
							userEmail={rowData?.createdByEmail || ""}
						/>
					</Box>
				),
			},
			{
				title: "",
				field: "" as any,
				align: "right",
				render: (rowData: GetSimulationsQuery["simulations"][number], _name: any, _action: any, isHovering: boolean) => (
					<SimulationTableRowButtons
						rowData={rowData}
						simulatorProductInputEnabled={!!simulatorProductInputEnabled}
						comparisonSimulations={comparisonSimulations}
						currentUserOwnerSimulations={currentUserOwnerSimulations}
						isHovering={isHovering}
					/>
				),
			},
		].filter(Boolean) as Array<Column<GetSimulationsQuery["simulations"][number]>>;

	return (
		<>
			<LyfeDataTable
				onRowClick={handleRowClick}
				title={title}
				hideRowHighlight
				isLoading={loading}
				data={data?.simulations || []}
				totalRecords={data?.simulations?.length || 0}
				columns={columns(t).filter(
					(col) => (col.field as any) === "" || (col.field && activeUser.simulationsTableColumns.includes(col.field))
				)}
				filteredRecords={data?.filteredSimulationCount[0]._count}
				toolbarProps={{ actionItems: [] }}
				page={page}
				defaultSort={defaultSort}
				rowsPerPage={rowsPerPage}
				preHeader={<SimulationTableHeader title={headerTitle} />}
				enableRowHovering
				viewMoreLimit={viewMoreLimit}
				viewMoreState={viewMoreState}
				handleViewMoreClick={handleViewMoreClick}
			/>
			{businessCaseSnackBarUpdateStatus.simulationId.map((id) => (
				<ErrorSnackbarAlert
					id={id}
					errorTitle={t("simulator-business-tag-update-error-p1", {
						simulationId: id,
					})}
					secondaryText={t("simulator-business-tag-update-error-p2")}
				/>
			))}
		</>
	);
};
